import * as React from "react";

import { Alert, AlertDescription } from "../alert";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../card";
import { Elevated } from "../elevated";
import { cn } from "../utils";

export interface FormShellProps {
  /** Card title — shown in the header. Omit for embedded/drawer forms. */
  title?: React.ReactNode;
  /** Optional one-line description under the title. */
  description?: React.ReactNode;
  /** Form submit handler. */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void | Promise<void>;
  /** Inline error rendered above the footer. */
  error?: string | null;
  /** Footer slot — typically Cancel + Submit. Right-aligned by default. */
  footer?: React.ReactNode;
  /**
   * Drop the outer Card wrapper. Use when embedding inside a drawer or
   * dialog that is already an Elevated surface.
   */
  compact?: boolean;
  /** Override the children container spacing (default: space-y-4). */
  contentClassName?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * The standard wrapper for any dedicated form. Provides a Card with header
 * (title + description), content (fields), inline error region, and a
 * footer button row. Use FormField for each input. Use `compact` when
 * embedded inside a drawer or dialog.
 */
export function FormShell({
  title,
  description,
  onSubmit,
  error,
  footer,
  compact,
  contentClassName,
  className,
  children,
}: FormShellProps) {
  const hasHeader = Boolean(title || description);

  const content = (
    <>
      {hasHeader && (
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent className={cn("space-y-4", contentClassName)}>
        {children}
      </CardContent>
      {error && (
        <div className="px-6">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}
      {footer && (
        <CardFooter className="justify-end gap-2">{footer}</CardFooter>
      )}
    </>
  );

  if (compact) {
    return (
      <form
        data-slot="form-shell"
        data-compact="true"
        onSubmit={onSubmit}
        className={cn("flex flex-col gap-6", className)}
      >
        {hasHeader && (
          <div className="flex flex-col gap-1">
            {title && <div className="text-foreground">{title}</div>}
            {description && (
              <div className="type-body text-muted-foreground">{description}</div>
            )}
          </div>
        )}
        <div className={cn("space-y-4", contentClassName)}>{children}</div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {footer && (
          <div className="flex items-center justify-end gap-2">{footer}</div>
        )}
      </form>
    );
  }

  return (
    <Elevated
      offset={1}
      shadowLevel={0}
      asChild
      data-slot="form-shell"
      className={cn(
        // FormShell is <Card>'s twin — same substrate, same job (a container
        // holding content) — so it wears the same treatment: own fill, hairline,
        // shadow pool, 16px. It used to paint the ladder's `bg-surface-2` +
        // `shadow-surface-2` ring, which tracked Card only because `--card` was
        // an alias for surface-2; once Card moved onto its own token the two
        // silently drifted apart. See packages/ui/src/card.tsx.
        "text-foreground flex flex-col gap-6 overflow-hidden rounded-xl border border-border bg-card py-6 shadow-card",
        className,
      )}
    >
      <form onSubmit={onSubmit}>{content}</form>
    </Elevated>
  );
}

export interface FormFieldProps {
  /** Label text. Pass null/false to omit the label. */
  label?: React.ReactNode;
  /** Show a small red dot after the label. */
  required?: boolean;
  /**
   * Right-aligned slot on the label row — for a small status/affordance that
   * belongs beside the label (e.g. an inline save indicator on an
   * auto-saving field). Renders the label row as a justified flex row.
   */
  labelAction?: React.ReactNode;
  /** Helper text under the input, before any error. */
  help?: React.ReactNode;
  /** Per-field error message. */
  error?: string;
  /**
   * Slot rendered below the help/error line — for per-field affordances that
   * sit under the input (e.g. an auto-fill provenance "Looks right / Wrong" row).
   */
  footer?: React.ReactNode;
  /** Muted label tone. Default is text-foreground. */
  muted?: boolean;
  /** Optional id linking label to input. */
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * One field within a FormShell. Renders label + child input + optional
 * help/error. Stack vertically. `labelAction` adds a right-aligned slot on
 * the label row; `footer` adds a slot below help/error — both used by
 * inline-editable surfaces (see BriefFieldRow) without forking the atom.
 */
export function FormField({
  label,
  required,
  labelAction,
  help,
  error,
  footer,
  muted,
  htmlFor,
  className,
  children,
}: FormFieldProps) {
  const labelEl = label ? (
    <label
      htmlFor={htmlFor}
      className={cn(
        "type-body",
        muted ? "text-muted-foreground" : "text-foreground",
      )}
    >
      {label}
      {required && (
        <span
          aria-hidden
          className="ml-1 inline-block size-1.5 rounded-full bg-destructive align-middle"
        />
      )}
    </label>
  ) : null;

  return (
    <div
      data-slot="form-field"
      className={cn("flex flex-col gap-1.5", className)}
    >
      {labelAction ? (
        <div className="flex items-center justify-between gap-2">
          {labelEl ?? <span />}
          <span className="shrink-0">{labelAction}</span>
        </div>
      ) : (
        labelEl
      )}
      {children}
      {help && !error && (
        <span className="type-subtext text-muted-foreground">{help}</span>
      )}
      {error && <span className="type-subtext text-destructive">{error}</span>}
      {footer}
    </div>
  );
}
